import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link, InfoTooltip } from 'ui';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import UsersDropdown from 'common/users/containers/users-dropdown-container';
import { defaultFilterValues } from '../../utils/app-configs';

const Title = styled.div`
  font-weight: 400;
  font-size: 16px;
  display: flex;
`;

const StyledDropdown = styled(UsersDropdown)`
  width: ${p => p.isColumnFilter ? '100%' : '90%'} !important;
  .ant-select-selection {
    padding: 0;
  }
`;

const MeLink = styled(Link)`
  margin: 0 4px;
`;

const NotMeLink = styled(Link)`
  margin-left: 4px;
`;

const PeopleFilter = ({
  description,
  onChange,
  activeFilters,
  disabled,
  userId,
  userFullName,
  intl,
  defaultMe,
  currentUserId,
  currentUserName,
  isColumnFilter,
  isRestricted,
  info = '',
}) => {
  const [value, setValue] = useState();
  const [inputValue, setInputValue] = useState('');
  const [clearUsersDataFn, setClearUsersDataFn] = useState(null);

  const selectMe = () => {
    clearUsersDataFn();
    onChange([defaultFilterValues.me]);
    setValue(userId);
    setInputValue(userFullName);
  };

  const selectNotMe = () => {
    clearUsersDataFn();
    onChange([defaultFilterValues.notMe]);
    setValue(defaultFilterValues.notMe);
    setInputValue(intl.formatMessage({ id: 'dashboard.draftsTable.toolbar.filter.NOT_ME' }));
  };

  useEffect(() => {
    if (defaultMe) {
      // Put in a timeout, to make sure the initial call is fired
      // (we don't care for results, as the saga will take the latest.)
      // Otherwise, this request can be fired before the initial one
      // As we don't know which componentDidMount will fire first.
      setTimeout(selectMe, 100);
    }

    if (currentUserId) {
      // If notMe is set, MW still sends back the current user's details in currentUserId & name
      // -> need to check if activeFilters has notMe and set those details instead the user's
      // -> otherwise just set the selected user's id & name
      const notMeFilter = activeFilters?.find(e => e.includes(defaultFilterValues.notMe));
      if (notMeFilter) {
        setValue(defaultFilterValues.notMe);
        setInputValue(intl.formatMessage({ id: 'dashboard.draftsTable.toolbar.filter.NOT_ME' }));
      } else {
        setValue(currentUserId);
        setInputValue(currentUserName);
      }
    }
  }, []);

  useEffect(() => {
    if (activeFilters?.length && activeFilters[0] !== value) {
      // only do this if the user is not typing (so one of the 2 fields is empty)
      if (!activeFilters[0]) {
        setValue(activeFilters[0]);
      }
    } else if (!activeFilters) {
      setValue();
      setInputValue('');
    }
  }, [activeFilters]);


  const handleSearch = (currentValue) => {
    // For clear purposes
    if (!currentValue) {
      onChange([currentValue]);
      setValue(currentValue);
      setInputValue('');
      clearUsersDataFn();
    } else {
      setInputValue(currentValue);
    }
  };

  const handleUserSelect = ({ value: currentValue, selectedUser }) => {
    onChange([currentValue]);
    setValue(currentValue);
    setInputValue(selectedUser.displayName);
  };

  return (
    <>
      <Title>
        {description.toUpperCase()}{' '}
        <MeLink onClick={selectMe}>
          {intl.formatMessage({ id: 'dashboard.draftsTable.toolbar.filter.ME' }).toUpperCase()}
        </MeLink>
        /
        <NotMeLink onClick={selectNotMe}>
          {intl.formatMessage({ id: 'dashboard.draftsTable.toolbar.filter.NOT_ME' }).toUpperCase()}
        </NotMeLink>
        <InfoTooltip content={info} />
      </Title>
      <StyledDropdown
        onSelect={handleUserSelect}
        onSearch={handleSearch}
        disabled={disabled}
        allowClear={true}
        value={inputValue}
        placeholder={intl.formatMessage({ id: 'ticket.SEARCH_PEOPLE' })}
        clearUsersDataCb={fn => setClearUsersDataFn(() => fn)}
        isColumnFilter={isColumnFilter}
        isRestricted={isRestricted}
      />
    </>
  );
};

const mapStateToProps = state => ({
  userId: state.user.id,
  userFullName: state.user.fullname,
});

export default connect(
  mapStateToProps,
  null,
)(injectIntl(PeopleFilter));

PeopleFilter.propTypes = {
  description: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  activeFilters: PropTypes.array,
  disabled: PropTypes.bool,
  userId: PropTypes.string,
  userFullName: PropTypes.string,
  intl: PropTypes.object,
  defaultMe: PropTypes.bool,
  currentUserId: PropTypes.string,
  currentUserName: PropTypes.string,
  isColumnFilter: PropTypes.bool,
  isRestricted: PropTypes.bool,
  info: PropTypes.string,
};
